import * as React from "react";
import { useParams } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import { makeStyles, shorthands, mergeClasses, Spinner } from "@fluentui/react-components";
import {
  Save20Regular,
  ArrowLeft20Regular,
  Checkmark20Filled,
  Circle20Regular,
  Clock20Regular,
  Person20Filled,
  VehicleCarRegular,
  Building20Regular,
  DocumentBulletList20Regular,
  Money20Regular,
  Add20Regular,
  Delete20Regular,
  Warning20Regular,
  CheckmarkCircle20Filled,
  PersonAccounts20Filled,
  Eye20Regular,
  EyeOff20Regular,
  CalendarClock20Regular,
} from "@fluentui/react-icons";
import {
  getSchadenmappe,
  updateDamage,
  createCostRow,
  updateCostRow,
  deleteCostRow,
  updateRequiredDocument,
  createRequiredDocument,
} from "../components/schadenmappe/api";
import type { SchadenmappeData, MappeTodo, MappeCostRow, MappeRequiredDocument } from "../components/schadenmappe/types";
import { DocumentsSection } from "../components/DocumentsSection";
import { openVorgangCreateFlow } from "../components/vorgang/create/VorgangCreateFlowWindow";
import { useWindowManager } from "../components/windowManager/WindowManagerContext";

const BRAND = {
  ink: "#2d142b",
  purple: "#4d2051",
  gold: "#f7a823",
  goldSoft: "rgba(247,168,35,0.15)",
  white: "#ffffff",
  border: "#E3E0E7",
  borderDashed: "#ECE7F0",
  muted: "#7c7480",
  bg: "#fafafa",
  cardBg: "#ffffff",
  fontPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
};

const useStyles = makeStyles({
  page: {
    backgroundColor: BRAND.bg,
    fontFamily: BRAND.fontPrimary,
    paddingBottom: "40px",
    minHeight: "calc(100vh - 60px)",
  },
  topBar: {
    backgroundColor: BRAND.white,
    borderBottomStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomColor: BRAND.borderDashed,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  backBtn: {
    appearance: "none",
    background: "transparent",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": { color: BRAND.ink },
  },
  saveBtn: {
    appearance: "none",
    background: BRAND.purple,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontWeight: 600,
    fontSize: "0.9rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("8px"),
    marginLeft: "auto",
    ":hover": { backgroundColor: "#3a1850" },
    ":disabled": { opacity: 0.6, cursor: "not-allowed" },
  },

  // ── Header card ──
  headerCard: {
    margin: "20px 20px 0",
    backgroundColor: BRAND.white,
    ...shorthands.border("2px", "solid", BRAND.ink),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding("20px", "24px"),
    boxShadow: "0 6px 18px rgba(45,20,43,0.07)",
  },
  headerTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "12px" },
  headerTitle: { margin: 0, fontFamily: BRAND.fontAccent, fontSize: "clamp(1.2rem, 2.5vw, 1.7rem)", fontWeight: 400, color: BRAND.ink },
  statusPill: { display: "inline-flex", alignItems: "center", gap: "6px", paddingTop: "5px", paddingBottom: "5px", paddingLeft: "12px", paddingRight: "12px", ...shorthands.borderRadius("999px"), fontFamily: BRAND.fontPrimary, fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.04em" },
  statusOpen: { backgroundColor: BRAND.goldSoft, color: "#7a4c00", ...shorthands.border("1px", "solid", BRAND.gold) },
  statusClosed: { backgroundColor: "rgba(46,125,79,0.12)", color: "#1c5434", ...shorthands.border("1px", "solid", "rgba(46,125,79,0.4)") },
  nbrRow: { display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "12px", fontFamily: BRAND.fontPrimary, fontSize: "0.88rem", color: BRAND.muted },
  nbrLabel: { fontWeight: 700, color: BRAND.purple },
  metaRow: { display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" },
  metaChip: { display: "inline-flex", alignItems: "center", gap: "5px", backgroundColor: BRAND.bg, color: BRAND.ink, fontFamily: BRAND.fontPrimary, fontSize: "0.82rem", fontWeight: 600, paddingTop: "4px", paddingBottom: "4px", paddingLeft: "10px", paddingRight: "10px", ...shorthands.border("1px", "solid", BRAND.border), ...shorthands.borderRadius("8px") },

  bodyGrid: {
    display: "grid", gridTemplateColumns: "minmax(260px, 320px) minmax(0, 1fr)",
    gap: "20px", margin: "16px 20px 0", alignItems: "start",
    "@media (max-width: 840px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  sidebar: { minWidth: 0, "@media (min-width: 841px)": { position: "sticky", top: "66px" } },
  mainColumn: { minWidth: 0 },

  // ── Generic section box ──
  box: {
    backgroundColor: BRAND.white,
    ...shorthands.border("2px", "solid", BRAND.ink),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding("16px"),
    boxShadow: "0 4px 12px rgba(45,20,43,0.06)",
    marginBottom: "16px",
  },
  boxHead: { display: "flex", alignItems: "center", columnGap: "10px", marginBottom: "12px", paddingBottom: "10px", borderBottomStyle: "solid", borderBottomWidth: "2px", borderBottomColor: BRAND.gold },
  boxTitle: { fontFamily: BRAND.fontAccent, fontSize: "1rem", fontWeight: 700, color: BRAND.ink, flexGrow: 1, margin: 0 },
  sectionToggle: {
    appearance: "none", background: "transparent", color: BRAND.ink, cursor: "pointer",
    flexShrink: 0, padding: "4px 8px", ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"), fontFamily: BRAND.fontPrimary, fontWeight: 600,
    ":hover": { backgroundColor: BRAND.goldSoft },
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: BRAND.gold, outlineOffset: "2px" },
  },
  sectionContent: { marginTop: "12px" },

  // ── Overview cards ──
  overviewGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "12px" },
  ovCard: { display: "flex", gap: "12px", alignItems: "flex-start", backgroundColor: BRAND.bg, ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("12px"), ...shorthands.padding("14px") },
  ovIcon: { width: "40px", height: "40px", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: BRAND.goldSoft, color: BRAND.purple, ...shorthands.borderRadius("10px") },
  ovLabel: { fontFamily: BRAND.fontPrimary, fontSize: "0.72rem", fontWeight: 700, color: BRAND.muted, letterSpacing: "0.06em", textTransform: "uppercase" },
  ovValue: { fontFamily: BRAND.fontPrimary, fontWeight: 700, color: BRAND.ink, fontSize: "0.98rem", marginTop: "2px" },
  ovMeta: { fontFamily: BRAND.fontPrimary, fontSize: "0.8rem", color: BRAND.muted, marginTop: "2px" },

  // ── Field grid ──
  fieldGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" },
  field: { display: "flex", flexDirection: "column", gap: "4px" },
  fieldLabel: { fontSize: "0.73rem", fontWeight: 700, color: BRAND.muted, textTransform: "uppercase", letterSpacing: "0.06em" },
  input: {
    appearance: "none", width: "100%", boxSizing: "border-box", backgroundColor: BRAND.white, color: BRAND.ink,
    fontFamily: BRAND.fontPrimary, fontSize: "0.9rem", ...shorthands.padding("8px", "10px"),
    ...shorthands.border("1.5px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("8px"),
    ":focus": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: BRAND.gold, outlineOffset: "1px", borderTopColor: BRAND.purple, borderRightColor: BRAND.purple, borderBottomColor: BRAND.purple, borderLeftColor: BRAND.purple },
  },
  textarea: { minHeight: "120px", resize: "vertical" },

  // ── Regulierer cards ──
  reguliererGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", "@media (max-width: 640px)": { gridTemplateColumns: "1fr" } },
  reguliererCard: { display: "flex", gap: "10px", alignItems: "flex-start", backgroundColor: BRAND.bg, ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("10px"), ...shorthands.padding("10px") },
  reguliererIcon: { width: "36px", height: "36px", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: BRAND.goldSoft, color: BRAND.purple, ...shorthands.borderRadius("8px"), flexShrink: 0 },
  reguliererRole: { fontFamily: BRAND.fontPrimary, fontSize: "0.73rem", fontWeight: 700, color: BRAND.muted, letterSpacing: "0.06em", textTransform: "uppercase" },
  reguliererName: { fontFamily: BRAND.fontPrimary, fontWeight: 600, color: BRAND.ink, fontSize: "0.92rem" },

  // ── Wiedervorlage ──
  wvlGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" },
  wvlCard: { ...shorthands.border("1.5px", "solid", BRAND.border), ...shorthands.borderRadius("10px"), ...shorthands.padding("12px"), display: "flex", flexDirection: "column", gap: "8px", backgroundColor: BRAND.white },
  wvlLabel: { display: "flex", alignItems: "center", gap: "6px", fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", fontWeight: 700, color: BRAND.ink },
  wvlDate: { fontFamily: BRAND.fontPrimary, fontSize: "0.95rem", fontWeight: 700, color: BRAND.purple },
  wvlBtn: { appearance: "none", cursor: "pointer", backgroundColor: "transparent", color: BRAND.purple, fontFamily: BRAND.fontPrimary, fontWeight: 600, fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "6px", ...shorthands.padding("5px", "8px"), ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("8px"), alignSelf: "flex-start", ":hover": { backgroundColor: BRAND.goldSoft } },
  timeline: { display: "flex", flexDirection: "column" },
  timelineItem: { display: "flex", gap: "10px", position: "relative" },
  timelineBody: { flex: 1, paddingBottom: "16px", minWidth: 0 },

  // ── Checkliste / Unterlagen ──
  unterlagenGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px" },
  unterlagenCard: { appearance: "none", width: "100%", textAlign: "left", ...shorthands.border("1.5px", "solid", BRAND.border), ...shorthands.borderRadius("10px"), ...shorthands.padding("12px"), display: "flex", flexDirection: "column", gap: "8px", backgroundColor: BRAND.white, cursor: "pointer", ":hover": { borderTopColor: BRAND.purple, borderRightColor: BRAND.purple, borderBottomColor: BRAND.purple, borderLeftColor: BRAND.purple }, ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: BRAND.gold, outlineOffset: "2px" } },
  unterlagenCardDone: { borderTopColor: "rgba(46,125,79,0.5)", borderRightColor: "rgba(46,125,79,0.5)", borderBottomColor: "rgba(46,125,79,0.5)", borderLeftColor: "rgba(46,125,79,0.5)", backgroundColor: "rgba(46,125,79,0.04)" },
  unterlagenLabel: { fontFamily: BRAND.fontPrimary, fontSize: "0.88rem", fontWeight: 600, color: BRAND.ink },
  unterlagenStatus: { display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.78rem", fontWeight: 700, ...shorthands.borderRadius("999px"), paddingTop: "3px", paddingBottom: "3px", paddingLeft: "8px", paddingRight: "8px" },
  unterlagenStatusOffen: { backgroundColor: BRAND.goldSoft, color: "#7a4c00" },
  unterlagenStatusDone: { backgroundColor: "rgba(46,125,79,0.12)", color: "#1c5434" },
  visibilityRow: { display: "flex", gap: "6px", flexWrap: "wrap" },
  visBadge: { display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "0.72rem", fontWeight: 600, ...shorthands.borderRadius("4px"), paddingTop: "2px", paddingBottom: "2px", paddingLeft: "6px", paddingRight: "6px", ...shorthands.border("1px", "solid", BRAND.borderDashed), color: BRAND.muted, backgroundColor: BRAND.bg },
  visBadgeActive: { color: BRAND.purple, backgroundColor: BRAND.goldSoft, borderTopColor: BRAND.gold, borderRightColor: BRAND.gold, borderBottomColor: BRAND.gold, borderLeftColor: BRAND.gold },

  // ── Vorprüfung ──
  vpRow: { display: "flex", alignItems: "center", gap: "10px", paddingTop: "8px", paddingBottom: "8px", borderBottomStyle: "solid", borderBottomWidth: "1px", borderBottomColor: BRAND.borderDashed },
  vpCheck: { width: "18px", height: "18px", flexShrink: 0, cursor: "pointer", accentColor: BRAND.purple },
  vpLabel: { fontFamily: BRAND.fontPrimary, fontSize: "0.9rem", color: BRAND.ink, minWidth: "230px" },
  vpInfo: { flex: 1, appearance: "none", backgroundColor: BRAND.bg, color: BRAND.ink, fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", ...shorthands.padding("5px", "8px"), ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("6px") },

  // ── Kosten ──
  costTable: { width: "100%", borderCollapse: "collapse", fontFamily: BRAND.fontPrimary, fontSize: "0.83rem" },
  costTh: { textAlign: "left", fontWeight: 700, color: BRAND.purple, fontSize: "0.73rem", letterSpacing: "0.05em", textTransform: "uppercase", ...shorthands.padding("6px", "8px"), borderBottomStyle: "solid", borderBottomWidth: "2px", borderBottomColor: BRAND.gold, whiteSpace: "nowrap" },
  costTd: { ...shorthands.padding("6px", "8px"), borderBottomStyle: "solid", borderBottomWidth: "1px", borderBottomColor: BRAND.borderDashed, color: BRAND.ink, verticalAlign: "middle" },
  costInput: { appearance: "none", width: "100%", minWidth: "70px", backgroundColor: BRAND.bg, color: BRAND.ink, fontFamily: BRAND.fontPrimary, fontSize: "0.83rem", ...shorthands.padding("4px", "6px"), ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("6px") },
  addRowBtn: { appearance: "none", cursor: "pointer", backgroundColor: "transparent", color: BRAND.purple, fontFamily: BRAND.fontPrimary, fontWeight: 600, fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "10px", ...shorthands.padding("6px", "10px"), ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("8px"), ":hover": { backgroundColor: BRAND.goldSoft } },
  deleteBtn: { appearance: "none", cursor: "pointer", background: "none", color: "#c84c4c", ...shorthands.border("none"), ...shorthands.padding("2px", "4px"), ...shorthands.borderRadius("4px"), ":hover": { backgroundColor: "rgba(200,76,76,0.1)" } },
  summaryInput: { appearance: "none", backgroundColor: BRAND.white, color: BRAND.ink, fontFamily: BRAND.fontPrimary, fontSize: "0.88rem", fontWeight: 600, ...shorthands.padding("5px", "8px"), ...shorthands.border("1px", "solid", BRAND.borderDashed), ...shorthands.borderRadius("6px"), width: "110px" },

  emptyState: { fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", color: BRAND.muted, paddingTop: "8px", paddingBottom: "8px" },
  actionError: { margin: "16px 20px 0", backgroundColor: "rgba(200,76,76,0.1)", color: "#8f2626", ...shorthands.border("1px", "solid", "rgba(200,76,76,0.35)"), ...shorthands.borderRadius("8px"), ...shorthands.padding("10px", "14px"), fontFamily: BRAND.fontPrimary, fontSize: "0.9rem" },

  loader: { display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", gap: "12px", fontFamily: BRAND.fontPrimary, color: BRAND.muted },
  errorMsg: { margin: "40px auto", maxWidth: "500px", textAlign: "center", fontFamily: BRAND.fontPrimary, color: "#c84c4c" },
});

// ── helpers ──
function eur(v: number | null | undefined): string {
  return (v ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(d?: string | null): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("de-DE"); } catch { return d; }
}
function computeRowGross(row: MappeCostRow): number {
  const net = Number(row.invoiceNet ?? 0);
  const tax = Number(row.taxRate ?? 19);
  return net * (1 + tax / 100);
}
function computeTotals(rows: MappeCostRow[], vatPct: number, sbFix: number, sbProzent: number) {
  let invoiceGrossSum = 0, deductionGrossSum = 0, paymentGrossSum = 0;
  for (const r of rows) {
    invoiceGrossSum += computeRowGross(r);
    deductionGrossSum += Number(r.deductionGross ?? 0);
    paymentGrossSum += Number(r.paymentGross ?? 0);
  }
  const costsGross = Math.max(0, invoiceGrossSum - deductionGrossSum);
  const vatDeduction = costsGross * (vatPct / 100);
  const intermediate = Math.max(0, costsGross - vatDeduction);
  const sbAmount = sbFix + intermediate * (sbProzent / 100);
  const compensation = Math.max(0, intermediate - sbAmount);
  const openPayments = Math.max(0, compensation - paymentGrossSum);
  return { invoiceGrossSum, deductionGrossSum, costsGross, vatDeduction, intermediate, sbAmount, compensation, alreadyPaid: paymentGrossSum, openPayments };
}

const CLOSED_STATUSES = ["G", "Geschlossen", "Abgeschlossen", "Erledigt"];
const isClosedStatus = (s?: string | null): boolean =>
  !!s && CLOSED_STATUSES.some((c) => c.toLowerCase() === String(s).trim().toLowerCase());
const statusLabel = (s?: string | null): string => {
  const v = (s ?? "").trim();
  if (!v) return "Offen";
  if (v === "O") return "Offen";
  if (v === "G") return "Geschlossen";
  return v;
};

const STATUS_OPTIONS = ["Offen", "In Bearbeitung", "Abgeschlossen", "Geschlossen", "Erledigt"];
const KASKO_OPTIONS = ["", "Kaskoschaden", "Teilkasko", "Vollkasko", "Haftpflicht"];
const DEFAULT_UNTERLAGEN = [
  { docKey: "fotos", label: "Fotos" },
  { docKey: "rechnungen", label: "Rechnung(en)" },
  { docKey: "arbeitsberichte", label: "Arbeitsberichte" },
  { docKey: "kva", label: "KVA" },
  { docKey: "polizeilicheMeldung", label: "Polizeiliche Meldung" },
  { docKey: "gutachten", label: "Gutachten" },
];

const WVL_SLOTS: { zg: MappeTodo["twvZielgruppe"]; label: string }[] = [
  { zg: "VN_AM_ZUG", label: "VN am Zug" },
  { zg: "VR_AM_ZUG", label: "VR / Regulierer am Zug" },
  { zg: "RS_INTERN", label: "RS intern" },
  { zg: "CO_MEETING", label: "CO-Meeting" },
];

const VP_ITEMS = [
  { key: "policeNeuster", label: "Police neuster Stand" },
  { key: "beitragBezahlt", label: "Beitrag bezahlt" },
  { key: "kulanz", label: "Kulanzzahlung möglich" },
  { key: "schadentagImVZ", label: "Schadentag im Versicherungszeitraum" },
  { key: "gefahrVersichert", label: "Gefahr versichert" },
] as const;

type VpState = Record<string, { checked: boolean; info: string }>;
type ViewToken = { damageId: number; generation: number };
type CostRowSaveState = {
  rowId: number;
  view: ViewToken;
  running: boolean;
  pending: boolean;
  deleting: boolean;
  completion: Promise<void> | null;
};

const nullableNumber = (value: string): number | null => value.trim() === "" ? null : Number(value);

function loadVorpruefung(id: number): VpState {
  try {
    const raw = localStorage.getItem(`schaden-vorpruefung-${id}`);
    if (raw) return JSON.parse(raw) as VpState;
  } catch (e) {
    console.warn("Vorprüfung konnte nicht geladen werden", e);
  }
  return {};
}

const riskTypeIcon = (t?: string | null) => {
  if (!t) return <DocumentBulletList20Regular />;
  if (/kfz|auto|fahr|vollkasko|teilkasko|haftpflicht/i.test(t)) return <VehicleCarRegular />;
  if (/immo|gebäude|geb|haus|leitungswasser|feuer|sturm|elementar/i.test(t)) return <Building20Regular />;
  return <DocumentBulletList20Regular />;
};

// ── Component ──
export default function Schadenmappe() {
  const { id } = useParams<{ id: string }>();
  const goBack = useSmartBack("/damages");
  const styles = useStyles();
  const { openWindow } = useWindowManager();
  const damageId = Number(id);
  const viewRef = React.useRef<ViewToken>({ damageId, generation: 0 });
  if (viewRef.current.damageId !== damageId) {
    viewRef.current = { damageId, generation: viewRef.current.generation + 1 };
  }
  const viewGeneration = viewRef.current.generation;
  const isCurrentView = React.useCallback((view: ViewToken) => (
    viewRef.current.damageId === view.damageId && viewRef.current.generation === view.generation
  ), []);

  const [data, setData] = React.useState<SchadenmappeData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [documentsOpen, setDocumentsOpen] = React.useState(false);
  const [regulationOpen, setRegulationOpen] = React.useState(true);
  const [costsOpen, setCostsOpen] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [creatingDefaultDocuments, setCreatingDefaultDocuments] = React.useState(false);
  const defaultDocumentsGenerationRef = React.useRef(0);
  const activeDefaultDocumentsBatchRef = React.useRef<number | null>(null);
  const loadGenerationRef = React.useRef(0);

  // editable Schaden fields
  const [fields, setFields] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState(false);

  // cost rows
  const [rows, setRows] = React.useState<MappeCostRow[]>([]);
  const rowsRef = React.useRef<MappeCostRow[]>([]);
  const [vatPct, setVatPct] = React.useState("");
  const [sbFix, setSbFix] = React.useState("");
  const [sbProzent, setSbProzent] = React.useState("");
  const costRowSaveStatesRef = React.useRef(new Map<number, CostRowSaveState>());
  const costRowRevisionsRef = React.useRef(new Map<number, number>());
  const requiredDocumentMutationsRef = React.useRef(new Set<string>());

  // Vorprüfung (lokal je Schaden persistiert)
  const [vp, setVp] = React.useState<VpState>({});

  // Wiedervorlage-Todo-Anlage

  const replaceRows = React.useCallback((updater: (current: MappeCostRow[]) => MappeCostRow[]) => {
    setRows((current) => {
      const next = updater(current);
      rowsRef.current = next;
      return next;
    });
  }, []);

  const reload = React.useCallback(async () => {
    const view = { damageId, generation: viewGeneration };
    if (!isCurrentView(view)) return;
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    setError(null);
    try {
      const d = await getSchadenmappe(damageId);
      if (generation !== loadGenerationRef.current || !isCurrentView(view)) return;
      setData(d);
      rowsRef.current = d.costRows ?? [];
      setRows(rowsRef.current);
      costRowSaveStatesRef.current.clear();
      costRowRevisionsRef.current.clear();
      setVatPct(d.damage.vorsteuerabzugPct != null ? String(d.damage.vorsteuerabzugPct) : "");
      setSbFix(d.damage.sbFix != null ? String(d.damage.sbFix) : "");
      setSbProzent(d.damage.sbProzent != null ? String(d.damage.sbProzent) : "");
      setFields({
        status: statusLabel(d.damage.status),
        damageType: d.damage.damageType ?? "",
        kaskoart: d.damage.kaskoart ?? "",
        claimAmount: d.damage.claimAmount != null ? String(d.damage.claimAmount) : "",
        damageDate: d.damage.damageDate ? d.damage.damageDate.slice(0, 10) : "",
        reportDate: d.damage.reportDate ? d.damage.reportDate.slice(0, 10) : "",
        description: d.damage.description ?? "",
        externalDamageNbr: d.damage.externalDamageNbr ?? "",
        title: d.damage.title ?? "",
      });
      setLoading(false);
    } catch (e) {
      if (generation !== loadGenerationRef.current || !isCurrentView(view)) return;
      setError(String(e));
      setLoading(false);
    }
  }, [damageId, isCurrentView, viewGeneration]);

  React.useEffect(() => {
    if (!damageId) return;
    defaultDocumentsGenerationRef.current += 1;
    activeDefaultDocumentsBatchRef.current = null;
    costRowSaveStatesRef.current.clear();
    costRowRevisionsRef.current.clear();
    requiredDocumentMutationsRef.current.clear();
    setCreatingDefaultDocuments(false);
    setSaving(false);
    setActionError(null);
    setVp(loadVorpruefung(damageId));
    void reload();
    return () => {
      loadGenerationRef.current += 1;
      defaultDocumentsGenerationRef.current += 1;
      activeDefaultDocumentsBatchRef.current = null;
    };
  }, [damageId, reload]);

  const setField = (k: string, v: string) => setFields((f) => ({ ...f, [k]: v }));

  const reportActionError = (context: string, cause: unknown) => {
    console.error(context, cause);
    setActionError(context);
  };

  const setVpItem = (key: string, patch: Partial<{ checked: boolean; info: string }>) => {
    setVp((prev) => {
      const current = prev[key] ?? { checked: false, info: "" };
      const next = { ...prev, [key]: { ...current, ...patch } };
      try { localStorage.setItem(`schaden-vorpruefung-${damageId}`, JSON.stringify(next)); } catch (e) { console.warn("Vorprüfung konnte nicht gespeichert werden", e); }
      return next;
    });
  };

  const saveSchaden = async () => {
    const view = { damageId, generation: viewGeneration };
    setSaving(true);
    setActionError(null);
    try {
      // Status nur als neuen Wert schreiben, wenn der Nutzer ihn geändert hat —
      // sonst den Original-Code ('O'/'G') erhalten, statt das Label zu schreiben.
      const origStatus = data?.damage?.status ?? "";
      const statusOut = fields.status === statusLabel(origStatus) ? (origStatus || undefined) : fields.status || undefined;
      const damagePatch = {
        status: statusOut,
        damageType: fields.damageType || null,
        kaskoart: fields.kaskoart || null,
        claimAmount: fields.claimAmount !== "" ? Number(fields.claimAmount) : null,
        damageDate: fields.damageDate || null,
        reportDate: fields.reportDate || null,
        description: fields.description || null,
        externalDamageNbr: fields.externalDamageNbr || null,
        title: fields.title || undefined,
        vorsteuerabzugPct: nullableNumber(vatPct),
        sbFix: nullableNumber(sbFix),
        sbProzent: nullableNumber(sbProzent),
      };
      await updateDamage(damageId, damagePatch);
      if (!isCurrentView(view)) return;
      // Kein Vollreload: Er würde parallel laufende Rechnungs-POST/PUT-States
      // und lokale temp-Zeilen ersetzen. Der erfolgreiche Damage-Patch reicht
      // aus, um die Kopf-/Schadendaten in dieser Mappe zu aktualisieren.
      setData((prev) => prev && prev.damage.id === view.damageId
        ? { ...prev, damage: { ...prev.damage, ...damagePatch } }
        : prev);
    } catch (cause) {
      if (isCurrentView(view)) reportActionError("Schaden konnte nicht gespeichert werden.", cause);
    } finally {
      if (isCurrentView(view)) setSaving(false);
    }
  };

  // ── cost row helpers ──
  rowsRef.current = rows;
  const updateRowLocal = (rowId: number, patch: Partial<MappeCostRow>) => {
    costRowRevisionsRef.current.set(rowId, (costRowRevisionsRef.current.get(rowId) ?? 0) + 1);
    replaceRows((current) => current.map((row) => row.id === rowId ? { ...row, ...patch } : row));
  };

  const drainRowSave = async (state: CostRowSaveState) => {
    if (state.running) return;
    state.running = true;
    let complete!: () => void;
    state.completion = new Promise<void>((resolve) => { complete = resolve; });
    try {
      while (state.pending && isCurrentView(state.view)) {
        state.pending = false;
        const row = rowsRef.current.find((candidate) => candidate.id === state.rowId);
        if (!row) break;
        const requestRevision = costRowRevisionsRef.current.get(state.rowId) ?? 0;
        if (row.id < 0) {
          const tempId = row.id;
          const saved = await createCostRow(state.view.damageId, row);
          if (state.deleting) {
            try {
              await deleteCostRow(state.view.damageId, saved.id);
            } catch (cause) {
              if (isCurrentView(state.view)) {
                replaceRows((current) => current.some((candidate) => candidate.id === saved.id)
                  ? current
                  : [...current, saved].sort((a, b) => a.pos - b.pos));
                reportActionError("Persistierte Rechnungszeile konnte nicht gelöscht werden.", cause);
              } else {
                console.error("Persistierte Rechnungszeile konnte nach Navigation nicht gelöscht werden.", cause);
              }
            }
            return;
          }
          if (!isCurrentView(state.view)) return;
          const latest = rowsRef.current.find((candidate) => candidate.id === tempId);
          if (!latest) {
            // Die Zeile wurde während des POST lokal gelöscht. Den inzwischen
            // angelegten Datensatz best-effort wieder entfernen.
            await deleteCostRow(state.view.damageId, saved.id).catch(() => undefined);
            return;
          }
          const latestRevision = costRowRevisionsRef.current.get(tempId) ?? requestRevision;
          const merged = { ...saved, ...latest, id: saved.id };
          replaceRows((current) => current.map((candidate) => candidate.id === tempId ? merged : candidate));
          costRowSaveStatesRef.current.delete(tempId);
          costRowRevisionsRef.current.delete(tempId);
          state.rowId = saved.id;
          costRowSaveStatesRef.current.set(saved.id, state);
          costRowRevisionsRef.current.set(saved.id, latestRevision);
          if (latestRevision > requestRevision) state.pending = true;
        } else {
          await updateCostRow(state.view.damageId, row.id, row);
          if (!isCurrentView(state.view)) return;
          if ((costRowRevisionsRef.current.get(row.id) ?? 0) > requestRevision) state.pending = true;
        }
      }
    } catch (cause) {
      // Ein während des fehlgeschlagenen Requests gesetztes pending darf nicht
      // automatisch einen zweiten POST/PUT starten. Erst ein neuer Benutzer-
      // Blur ist ein expliziter Retry.
      state.pending = false;
      if (!state.deleting && isCurrentView(state.view)) reportActionError("Rechnungszeile konnte nicht gespeichert werden.", cause);
    } finally {
      state.running = false;
      state.completion = null;
      complete();
      if (!state.deleting && state.pending && isCurrentView(state.view)) void drainRowSave(state);
    }
  };

  const saveRow = (rowId: number) => {
    setActionError(null);
    const existing = costRowSaveStatesRef.current.get(rowId);
    const state = existing ?? {
      rowId,
      view: { damageId, generation: viewGeneration },
      running: false,
      pending: false,
      deleting: false,
      completion: null,
    };
    if (!existing) costRowSaveStatesRef.current.set(rowId, state);
    state.pending = true;
    void drainRowSave(state);
  };
  const addRow = () =>
    replaceRows((current) => [...current, { id: -(Date.now()), pos: current.length, taxRate: 19, invoiceNet: 0, deductionGross: 0, paymentGross: 0 }]);
  const removeRow = async (rowId: number) => {
    const view = { damageId, generation: viewGeneration };
    const row = rowsRef.current.find((candidate) => candidate.id === rowId);
    if (!row) return;
    setActionError(null);
    const idx = rowsRef.current.findIndex((candidate) => candidate.id === rowId);
    const saveState = costRowSaveStatesRef.current.get(rowId);
    const pendingSave = saveState?.completion ?? null;
    if (saveState) {
      saveState.pending = false;
      saveState.deleting = true;
    }
    replaceRows((current) => current.filter((candidate) => candidate.id !== rowId));
    if (row.id <= 0) {
      if (costRowSaveStatesRef.current.get(row.id) === saveState) {
        costRowSaveStatesRef.current.delete(row.id);
        costRowRevisionsRef.current.delete(row.id);
      }
      return;
    }
    try {
      if (pendingSave) await pendingSave;
      await deleteCostRow(view.damageId, row.id);
    } catch (cause) {
      if (!isCurrentView(view)) return;
      replaceRows((current) => {
        if (current.some((candidate) => candidate.id === row.id)) return current;
        const restored = [...current];
        restored.splice(Math.min(idx, restored.length), 0, row);
        return restored;
      });
      reportActionError("Rechnungszeile konnte nicht gelöscht werden.", cause);
    } finally {
      if (costRowSaveStatesRef.current.get(row.id) === saveState) {
        costRowSaveStatesRef.current.delete(row.id);
        costRowRevisionsRef.current.delete(row.id);
      }
    }
  };

  const toggleReqDoc = async (doc: MappeRequiredDocument) => {
    const view = { damageId, generation: viewGeneration };
    const mutationKey = `${view.generation}:${doc.id}`;
    if (requiredDocumentMutationsRef.current.has(mutationKey)) return;
    requiredDocumentMutationsRef.current.add(mutationKey);
    const newStatus: "offen" | "eingereicht" = doc.status === "offen" ? "eingereicht" : "offen";
    setActionError(null);
    try {
      await updateRequiredDocument(view.damageId, doc.id, { status: newStatus });
      if (!isCurrentView(view)) return;
      setData((prev) => prev && prev.damage.id === view.damageId
        ? { ...prev, requiredDocuments: prev.requiredDocuments.map((d) => (d.id === doc.id ? { ...d, status: newStatus } : d)) }
        : prev);
    } catch (cause) {
      if (isCurrentView(view)) reportActionError("Unterlagenstatus konnte nicht gespeichert werden.", cause);
    } finally {
      requiredDocumentMutationsRef.current.delete(mutationKey);
    }
  };

  if (error) return <div className={styles.errorMsg}><Warning20Regular style={{ fontSize: 32, marginBottom: 8 }} /><br />{error}</div>;
  if (loading || data?.damage.id !== damageId) return <div className={styles.loader}><Spinner size="medium" /> Schadenmappe wird geladen…</div>;
  if (!data) return <div className={styles.errorMsg}><Warning20Regular style={{ fontSize: 32, marginBottom: 8 }} /><br />Schaden nicht gefunden</div>;

  const { damage, requiredDocuments } = data;
  const todos: MappeTodo[] = data.todos ?? [];
  const isDone = (t: MappeTodo) => isClosedStatus(t.status);
  const open = !isClosedStatus(damage.status);
  const totals = computeTotals(rows, vatPct.trim() === "" ? 10 : Number(vatPct), Number(sbFix || 0), Number(sbProzent || 0));

  const existingDocumentKeys = new Set(requiredDocuments.map((doc) => doc.docKey));
  const missingDefaultUnterlagen = DEFAULT_UNTERLAGEN.filter((doc) => !existingDocumentKeys.has(doc.docKey));
  const initDefaultUnterlagen = async () => {
    if (activeDefaultDocumentsBatchRef.current !== null || missingDefaultUnterlagen.length === 0) return;
    const batchDamageId = damageId;
    const batchView = { damageId, generation: viewGeneration };
    const generation = ++defaultDocumentsGenerationRef.current;
    activeDefaultDocumentsBatchRef.current = generation;
    setCreatingDefaultDocuments(true);
    setActionError(null);
    const failures: unknown[] = [];
    const nextSortOrder = requiredDocuments.reduce(
      (highest, document) => Math.max(highest, Number(document.sortOrder)),
      -1,
    ) + 1;
    try {
      for (const [index, doc] of missingDefaultUnterlagen.entries()) {
        if (defaultDocumentsGenerationRef.current !== generation || !isCurrentView(batchView)) return;
        try {
          const created = await createRequiredDocument(batchDamageId, {
            ...doc,
            status: "offen",
            visibleToCustomer: false,
            visibleToRegulierer: false,
            internalOnly: true,
            sortOrder: nextSortOrder + index,
          });
          if (defaultDocumentsGenerationRef.current !== generation || !isCurrentView(batchView)) return;
          setData((prev) => {
            if (!prev || prev.damage.id !== batchDamageId || prev.requiredDocuments.some((existing) => existing.docKey === created.docKey)) return prev;
            return {
              ...prev,
              requiredDocuments: [...prev.requiredDocuments, created].sort((a, b) => a.sortOrder - b.sortOrder),
            };
          });
        } catch (cause) {
          failures.push(cause);
        }
      }
      if (failures.length > 0 && defaultDocumentsGenerationRef.current === generation && isCurrentView(batchView)) {
        reportActionError("Standardunterlagen konnten nicht vollständig angelegt werden.", failures[0]);
      }
    } finally {
      if (activeDefaultDocumentsBatchRef.current === generation) {
        activeDefaultDocumentsBatchRef.current = null;
        setCreatingDefaultDocuments(false);
      }
    }
  };

  const risikoDisplay = damage.riskObject?.name ?? damage.riskObject?.riskType ?? damage.damageType ?? "Kein Risikoobjekt";
  const kontaktDisplay = damage.contact?.displayName ?? (damage.contact ? `Kontakt #${damage.contact.id}` : "Kein Kontakt");
  const vertragDisplay = damage.contract?.contractNbr ?? "Kein Vertrag";
  const isKfz = /kfz|kasko|haftpflicht|fahr|auto/i.test(`${damage.damageType ?? ""} ${damage.riskObject?.riskType ?? ""}`);

  const wvlTodoFor = (zg: MappeTodo["twvZielgruppe"]): MappeTodo | undefined => {
    const matches = todos.filter((t) => t.twvZielgruppe === zg && t.twv);
    if (matches.length === 0) return undefined;
    return matches.sort((a, b) => Date.parse(b.twv ?? "") - Date.parse(a.twv ?? ""))[0];
  };

  // Create-Rework CR-SP7: statt Todo-Modal den zweistufigen Erstell-Flow öffnen.
  const openWvlTodo = (slot: (typeof WVL_SLOTS)[number]) => {
    const existing = wvlTodoFor(slot.zg);
    openVorgangCreateFlow(openWindow, {
      initialContactId: damage.contact?.id ?? null,
      initialContactName: kontaktDisplay || null,
      initialSubject: `Wiedervorlage ${slot.label}: ${damage.damageNbr ?? `Schaden #${damageId}`}`,
      initialTwv: existing?.twv?.slice(0, 10) ?? null,
      initialTwvZielgruppe: slot.zg,
      linkedEntityType: "SCHADEN",
      linkedEntityId: damageId,
    });
  };

  const sectionHeading = (
    title: string,
    expanded: boolean,
    setExpanded: React.Dispatch<React.SetStateAction<boolean>>,
    icon: React.ReactNode,
  ) => (
    <>
      {icon}<h2 className={styles.boxTitle}>{title}</h2>
      <button type="button" className={styles.sectionToggle} aria-expanded={expanded}
        aria-label={`${title} ${expanded ? "ausblenden" : "einblenden"}`}
        onClick={() => setExpanded((value) => !value)}>
        {expanded ? "Ausblenden" : "Einblenden"}
      </button>
    </>
  );

  return (
    <div className={styles.page}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <button type="button" className={styles.backBtn} onClick={goBack}>
          <ArrowLeft20Regular /> Schäden
        </button>
        <span style={{ color: BRAND.muted, fontSize: "0.85rem" }}>/</span>
        <span style={{ color: BRAND.ink, fontSize: "0.88rem", fontWeight: 600 }}>
          {damage.damageNbr ?? `Schaden #${damageId}`}
        </span>
        <button type="button" className={styles.saveBtn} onClick={saveSchaden} disabled={saving}>
          <Save20Regular /> {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>

      {/* Header card */}
      <div className={styles.headerCard}>
        <div className={styles.headerTop}>
          <h1 className={styles.headerTitle}>{damage.title || damage.damageType || `Schaden #${damageId}`}</h1>
          <span className={mergeClasses(styles.statusPill, open ? styles.statusOpen : styles.statusClosed)}>
            {open ? <Clock20Regular style={{ fontSize: 14 }} /> : <CheckmarkCircle20Filled style={{ fontSize: 14 }} />}
            {statusLabel(damage.status)}
          </span>
        </div>
        <div className={styles.nbrRow}>
          <span><span className={styles.nbrLabel}>Nr. intern:</span> {damage.damageNbr ?? "—"}</span>
          <span><span className={styles.nbrLabel}>Nr. VR:</span> {damage.externalDamageNbr ?? "—"}</span>
          <span><span className={styles.nbrLabel}>Schadentag:</span> {fmtDate(damage.damageDate)}</span>
          <span><span className={styles.nbrLabel}>Gemeldet:</span> {fmtDate(damage.reportDate)}</span>
        </div>
        <div className={styles.metaRow}>
          {damage.damageType && <span className={styles.metaChip}>{riskTypeIcon(damage.damageType)} {damage.damageType}</span>}
          {damage.riskObject && <span className={styles.metaChip}>{riskTypeIcon(damage.riskObject.riskType)} Risiko: {risikoDisplay}</span>}
          {damage.contract && <span className={styles.metaChip}><DocumentBulletList20Regular /> {damage.contract.contractNbr}{damage.contract.branch ? ` · ${damage.contract.branch}` : ""}</span>}
          {damage.contact && <span className={styles.metaChip}><Person20Filled /> {kontaktDisplay}</span>}
          <span className={styles.metaChip}><Money20Regular /> Schadenhöhe: {eur(damage.claimAmount)}</span>
        </div>
      </div>

      {actionError && <div role="alert" className={styles.actionError}>{actionError}</div>}

      <div className={styles.bodyGrid}>
        <aside className={styles.sidebar} aria-label="Bearbeitungsverlauf des Schadens">
          <div className={styles.box}>
            <div className={styles.boxHead}>
              <DocumentBulletList20Regular style={{ color: BRAND.gold, fontSize: 20 }} />
              <h2 className={styles.boxTitle}>Schadenvorgang</h2>
              <button type="button" className={styles.wvlBtn} onClick={() => openVorgangCreateFlow(openWindow, { initialContactId: damage.contact?.id ?? null, initialContactName: kontaktDisplay || null, initialSubject: `Vorgang: ${damage.damageNbr ?? `Schaden #${damageId}`}`, linkedEntityType: "SCHADEN", linkedEntityId: damageId })}><Add20Regular /> Neuer Vorgang</button>
            </div>
            {todos.length === 0 ? (
              <span className={styles.emptyState}>Keine Vorgänge zu diesem Schaden.</span>
            ) : (
              <div className={styles.timeline}>
                {[...todos].sort((a, b) => Date.parse(b.twv ?? b.frist ?? "") - Date.parse(a.twv ?? a.frist ?? "")).map((todo, idx, sortedTodos) => {
                  const done = isDone(todo);
                  const isLast = idx === sortedTodos.length - 1;
                  return (
                    <div key={todo.id} className={styles.timelineItem}>
                      {!isLast && <div style={{ position: "absolute", left: 11, top: 28, bottom: 0, width: 2, backgroundColor: BRAND.borderDashed }} />}
                      <div style={{ flexShrink: 0, width: 24, height: 24, marginTop: 6, borderRadius: "50%", zIndex: 1, backgroundColor: done ? "#2E7D4F" : BRAND.gold, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {done ? <Checkmark20Filled style={{ fontSize: 13, color: "#fff" }} /> : <Circle20Regular style={{ fontSize: 13, color: BRAND.ink }} />}
                      </div>
                      <div className={styles.timelineBody}>
                        <div style={{ fontSize: "0.88rem", fontWeight: 600, color: BRAND.ink, textDecoration: done ? "line-through" : "none" }}>{todo.titel}</div>
                        <div style={{ fontSize: "0.78rem", color: BRAND.muted, marginTop: 2 }}>
                          {todo.twv ? `Wiedervorlage ${fmtDate(todo.twv)}` : todo.frist ? `Frist ${fmtDate(todo.frist)}` : ""}{todo.status ? ` · ${todo.status}` : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.box}>
            <div className={styles.boxHead}><CalendarClock20Regular style={{ color: BRAND.gold, fontSize: 20 }} /><h2 className={styles.boxTitle}>Wiedervorlagen</h2></div>
            <div className={styles.wvlGrid}>
              {WVL_SLOTS.map((slot) => {
                const todo = wvlTodoFor(slot.zg);
                return (
                  <div key={slot.zg ?? slot.label} className={styles.wvlCard}>
                    <div className={styles.wvlLabel}><Clock20Regular style={{ fontSize: 15, color: BRAND.purple }} /> {slot.label}</div>
                    <div className={styles.wvlDate}>{todo ? fmtDate(todo.twv) : "—"}</div>
                    {todo && <div className={styles.ovMeta}>{todo.titel}</div>}
                    <button type="button" className={styles.wvlBtn} onClick={() => openWvlTodo(slot)}><Add20Regular /> Vorgang anlegen</button>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        <section className={styles.mainColumn} aria-label="Schadendetails">
          {/* Overview */}
          <div className={styles.box}>
            <div className={styles.boxHead}><DocumentBulletList20Regular style={{ color: BRAND.gold, fontSize: 20 }} /><h2 className={styles.boxTitle}>Risiko, Kontakt und Vertrag</h2></div>
            <div className={styles.overviewGrid}>
              <div className={styles.ovCard}>
                <span className={styles.ovIcon}>{riskTypeIcon(damage.riskObject?.riskType ?? damage.damageType)}</span>
                <div><div className={styles.ovLabel}>Risiko</div><div className={styles.ovValue}>{risikoDisplay}</div><div className={styles.ovMeta}>{damage.riskObject?.riskType ?? damage.damageType ?? "—"}</div></div>
              </div>
              <div className={styles.ovCard}>
                <span className={styles.ovIcon}><Person20Filled /></span>
                <div><div className={styles.ovLabel}>Kontakt</div><div className={styles.ovValue}>{kontaktDisplay}</div><div className={styles.ovMeta}>Geschädigter / Kunde</div></div>
              </div>
              <div className={styles.ovCard}>
                <span className={styles.ovIcon}><DocumentBulletList20Regular /></span>
                <div><div className={styles.ovLabel}>Vertrag</div><div className={styles.ovValue}>{vertragDisplay}</div><div className={styles.ovMeta}>{damage.contract?.branch ?? "—"}</div></div>
              </div>
            </div>
          </div>

          {/* Schaden fields */}
          <div className={styles.box}>
            <div className={styles.boxHead}><VehicleCarRegular style={{ color: BRAND.gold, fontSize: 20 }} /><h2 className={styles.boxTitle}>Schaden</h2></div>
            <div className={styles.fieldGrid}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Status</span>
                <select className={styles.input} value={fields.status ?? ""} onChange={(e) => setField("status", e.target.value)}>
                  {!STATUS_OPTIONS.includes(fields.status) && fields.status ? <option value={fields.status}>{fields.status}</option> : null}
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Schadensart</span>
                <input className={styles.input} value={fields.damageType ?? ""} placeholder="z. B. Leitungswasser" onChange={(e) => setField("damageType", e.target.value)} />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Meldedatum</span>
                <input type="date" className={styles.input} value={fields.reportDate ?? ""} onChange={(e) => setField("reportDate", e.target.value)} />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Schadentag</span>
                <input type="date" className={styles.input} value={fields.damageDate ?? ""} onChange={(e) => setField("damageDate", e.target.value)} />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Schadenhöhe (€)</span>
                <input type="number" className={styles.input} value={fields.claimAmount ?? ""} placeholder="Betrag" onChange={(e) => setField("claimAmount", e.target.value)} />
              </label>
              {isKfz ? (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Kaskoart</span>
                  <select className={styles.input} value={fields.kaskoart ?? ""} onChange={(e) => setField("kaskoart", e.target.value)}>
                    {KASKO_OPTIONS.map((k) => <option key={k} value={k}>{k || "— wählen —"}</option>)}
                  </select>
                </label>
              ) : (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Schaden-Nr. VR</span>
                  <input className={styles.input} value={fields.externalDamageNbr ?? ""} placeholder="VR-Schadennummer" onChange={(e) => setField("externalDamageNbr", e.target.value)} />
                </label>
              )}
            </div>
            <label className={styles.field} style={{ marginTop: 14 }}>
              <span className={styles.fieldLabel}>Schadenbeschreibung</span>
              <textarea className={mergeClasses(styles.input, styles.textarea)} value={fields.description ?? ""} placeholder="Beschreibung / Schadenursache" onChange={(e) => setField("description", e.target.value)} />
            </label>
          </div>

          {/* VR & Bearbeiter */}
          <div className={styles.box}>
            <div className={styles.boxHead}><PersonAccounts20Filled style={{ color: BRAND.gold, fontSize: 20 }} /><h2 className={styles.boxTitle}>VR &amp; Schadenbearbeiter</h2></div>
            <div className={styles.reguliererGrid}>
              {damage.reguliererUserId && (
                <div className={styles.reguliererCard}><span className={styles.reguliererIcon}><Person20Filled style={{ fontSize: 18 }} /></span><div><div className={styles.reguliererRole}>Sachbearbeiter (intern)</div><div className={styles.reguliererName}>{damage.reguliererUserId}</div></div></div>
              )}
              {damage.reguliererContact && (
                <div className={styles.reguliererCard}><span className={styles.reguliererIcon}><PersonAccounts20Filled style={{ fontSize: 18 }} /></span><div><div className={styles.reguliererRole}>Schadenregulierer</div><div className={styles.reguliererName}>{damage.reguliererContact.displayName}</div></div></div>
              )}
              {damage.sachverstaendigerContact && (
                <div className={styles.reguliererCard}><span className={styles.reguliererIcon}><Person20Filled style={{ fontSize: 18 }} /></span><div><div className={styles.reguliererRole}>Sachverständiger (VR)</div><div className={styles.reguliererName}>{damage.sachverstaendigerContact.displayName}</div></div></div>
              )}
              {damage.sachbearbeiterVrContact && (
                <div className={styles.reguliererCard}><span className={styles.reguliererIcon}><Person20Filled style={{ fontSize: 18 }} /></span><div><div className={styles.reguliererRole}>Sachbearbeiter VR</div><div className={styles.reguliererName}>{damage.sachbearbeiterVrContact.displayName}</div></div></div>
              )}
              {!damage.reguliererUserId && !damage.reguliererContact && !damage.sachverstaendigerContact && !damage.sachbearbeiterVrContact && (
                <span className={styles.emptyState}>Kein Regulierer / Bearbeiter hinterlegt.</span>
              )}
            </div>
          </div>

          {/* Dokumente */}
          <div className={styles.box}>
            <div className={styles.boxHead}>{sectionHeading("Dokumente", documentsOpen, setDocumentsOpen, <DocumentBulletList20Regular style={{ color: BRAND.gold, fontSize: 20 }} />)}</div>
            {documentsOpen && <div className={styles.sectionContent}>
              {damage.contact?.id ? <DocumentsSection contactId={damage.contact.id} /> : <span className={styles.emptyState}>Diesem Schaden ist kein Kontakt zugeordnet — keine Dokumente verfügbar.</span>}
            </div>}
          </div>

          {/* Einzureichende Unterlagen */}
          <div className={styles.box}>
            <div className={styles.boxHead}><DocumentBulletList20Regular style={{ color: BRAND.gold, fontSize: 20 }} /><h2 className={styles.boxTitle}>Einzureichende Unterlagen</h2>
              {missingDefaultUnterlagen.length > 0 && <button type="button" className={styles.wvlBtn} style={{ marginLeft: "auto" }} onClick={initDefaultUnterlagen} disabled={creatingDefaultDocuments}><Add20Regular /> {creatingDefaultDocuments ? "Standardunterlagen werden angelegt…" : "Standardunterlagen anlegen"}</button>}
            </div>
            {requiredDocuments.length === 0 ? (
              <span className={styles.emptyState}>Noch keine einzureichenden Unterlagen angelegt.</span>
            ) : <div className={styles.unterlagenGrid}>
              {requiredDocuments.map((doc) => {
                const cardContent = <>
                  <span className={styles.unterlagenLabel}>
                    {doc.status === "eingereicht" ? <Checkmark20Filled style={{ color: "#1c5434", marginRight: 4 }} /> : <Circle20Regular style={{ color: BRAND.gold, marginRight: 4 }} />}
                    {doc.label}
                  </span>
                  <span className={mergeClasses(styles.unterlagenStatus, doc.status === "eingereicht" ? styles.unterlagenStatusDone : styles.unterlagenStatusOffen)}>{doc.status === "eingereicht" ? "Eingereicht" : "Ausstehend"}</span>
                  {doc.id > 0 && <span className={styles.visibilityRow}>
                    <span className={mergeClasses(styles.visBadge, doc.visibleToCustomer ? styles.visBadgeActive : "")}>{doc.visibleToCustomer ? <Eye20Regular style={{ fontSize: 12 }} /> : <EyeOff20Regular style={{ fontSize: 12 }} />} Kunde</span>
                    <span className={mergeClasses(styles.visBadge, doc.visibleToRegulierer ? styles.visBadgeActive : "")}>{doc.visibleToRegulierer ? <Eye20Regular style={{ fontSize: 12 }} /> : <EyeOff20Regular style={{ fontSize: 12 }} />} Regulierer</span>
                    <span className={mergeClasses(styles.visBadge, doc.internalOnly ? styles.visBadgeActive : "")}>Intern</span>
                  </span>}
                </>;
                const className = mergeClasses(styles.unterlagenCard, doc.status === "eingereicht" ? styles.unterlagenCardDone : "");
                return <button key={doc.id} type="button" className={className} onClick={() => toggleReqDoc(doc)}>{cardContent}</button>;
              })}
            </div>}
          </div>

          {/* Vorprüfung */}
          <div className={styles.box}>
            <div className={styles.boxHead}><CheckmarkCircle20Filled style={{ color: BRAND.gold, fontSize: 20 }} /><h2 className={styles.boxTitle}>Vorprüfung</h2></div>
            {VP_ITEMS.map((item) => {
              const state = vp[item.key] ?? { checked: false, info: "" };
              return <div key={item.key} className={styles.vpRow}>
                <input type="checkbox" className={styles.vpCheck} checked={state.checked} onChange={(e) => setVpItem(item.key, { checked: e.target.checked })} aria-label={item.label} />
                <span className={styles.vpLabel}>{item.label}</span>
                <input className={styles.vpInfo} value={state.info} placeholder="Notiz / Info" aria-label={`Notiz zu ${item.label}`} onChange={(e) => setVpItem(item.key, { info: e.target.value })} />
              </div>;
            })}
          </div>

          {/* Regulierung */}
          <div className={styles.box}>
            <div className={styles.boxHead}>{sectionHeading("Regulierung — Rechnungen", regulationOpen, setRegulationOpen, <Money20Regular style={{ color: BRAND.gold, fontSize: 20 }} />)}</div>
            {regulationOpen && <div className={styles.sectionContent}>
            <div style={{ overflowX: "auto" }}>
              <table className={styles.costTable}>
                <thead>
                  <tr>{["Pos", "Aussteller", "R-Nr", "R-Datum", "Kurztext", "MwSt%", "Netto", "Brutto", ""].map((h) => <th key={h} className={styles.costTh}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const gross = computeRowGross(row);
                    return (
                      <tr key={row.id ?? idx}>
                        <td className={styles.costTd}>{idx + 1}</td>
                        <td className={styles.costTd}><input aria-label={`Rechnung ${idx + 1} Aussteller`} className={styles.costInput} style={{ minWidth: 80 }} value={row.issuer ?? ""} onChange={(e) => updateRowLocal(row.id, { issuer: e.target.value })} onBlur={() => saveRow(row.id)} placeholder="Aussteller" /></td>
                        <td className={styles.costTd}><input aria-label={`Rechnung ${idx + 1} Rechnungsnummer`} className={styles.costInput} style={{ minWidth: 70 }} value={row.invoiceNumber ?? ""} onChange={(e) => updateRowLocal(row.id, { invoiceNumber: e.target.value })} onBlur={() => saveRow(row.id)} placeholder="R-Nr" /></td>
                        <td className={styles.costTd}><input aria-label={`Rechnung ${idx + 1} Rechnungsdatum`} className={styles.costInput} type="date" value={row.invoiceDate ? String(row.invoiceDate).slice(0, 10) : ""} onChange={(e) => updateRowLocal(row.id, { invoiceDate: e.target.value })} onBlur={() => saveRow(row.id)} /></td>
                        <td className={styles.costTd}><input aria-label={`Rechnung ${idx + 1} Kurztext`} className={styles.costInput} style={{ minWidth: 90 }} value={row.shortText ?? ""} onChange={(e) => updateRowLocal(row.id, { shortText: e.target.value })} onBlur={() => saveRow(row.id)} placeholder="Kurztext" /></td>
                        <td className={styles.costTd}><input aria-label={`Rechnung ${idx + 1} Mehrwertsteuer Prozent`} className={styles.costInput} style={{ width: 50 }} type="number" value={row.taxRate} onChange={(e) => updateRowLocal(row.id, { taxRate: Number(e.target.value) })} onBlur={() => saveRow(row.id)} /></td>
                        <td className={styles.costTd}><input aria-label={`Rechnung ${idx + 1} Nettobetrag`} className={styles.costInput} style={{ width: 75 }} type="number" value={row.invoiceNet} onChange={(e) => updateRowLocal(row.id, { invoiceNet: Number(e.target.value) })} onBlur={() => saveRow(row.id)} /></td>
                        <td className={styles.costTd} style={{ fontWeight: 600 }}>{gross.toFixed(2)}</td>
                        <td className={styles.costTd}><button type="button" className={styles.deleteBtn} onClick={() => removeRow(row.id)} aria-label="Zeile löschen"><Delete20Regular /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className={styles.addRowBtn} onClick={addRow}><Add20Regular /> Zeile hinzufügen</button>
            </div>}
          </div>

          {/* Kosten und Abrechnung */}
          <div className={styles.box}>
            <div className={styles.boxHead}>{sectionHeading("Kosten und Abrechnung", costsOpen, setCostsOpen, <Money20Regular style={{ color: BRAND.gold, fontSize: 20 }} />)}</div>
            {costsOpen && <div className={styles.sectionContent}>
            <div className={styles.boxHead}><Money20Regular style={{ color: BRAND.gold, fontSize: 20 }} /><span className={styles.boxTitle}>Abrechnung</span></div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14, fontSize: "0.83rem" }}>
              {[{ label: "Vorsteuerabzug %", ariaLabel: "Vorsteuerabzug Prozent", value: vatPct, set: setVatPct }, { label: "SB Fix (€)", ariaLabel: "Selbstbeteiligung fix Euro", value: sbFix, set: setSbFix }, { label: "SB Prozentual (%)", ariaLabel: "Selbstbeteiligung prozentual Prozent", value: sbProzent, set: setSbProzent }].map((f) => (
                <label key={f.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: BRAND.muted, fontWeight: 600 }}>{f.label}</span>
                  <input aria-label={f.ariaLabel} type="number" className={styles.summaryInput} value={f.value} onChange={(e) => f.set(e.target.value)} />
                </label>
              ))}
            </div>
            <div style={{ borderTop: `1.5px solid ${BRAND.borderDashed}`, paddingTop: 12 }}>
              {[
                { label: "Kosten insgesamt (Σ Rechnungen bto.)", value: eur(totals.invoiceGrossSum), bold: false, op: null },
                { label: "Selbstbeteiligung", value: `− ${eur(totals.sbAmount)}`, bold: false, op: "−" },
                { label: "Zahlung / Entschädigung", value: eur(totals.compensation), bold: true, op: "=" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: r.op === "=" ? "none" : `1px solid ${BRAND.borderDashed}`, borderTop: r.op === "=" ? `2px solid ${BRAND.ink}` : "none" }}>
                  <span style={{ fontSize: "0.9rem", color: r.bold ? BRAND.ink : BRAND.muted, fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
                  <span style={{ fontSize: r.bold ? "1.05rem" : "0.9rem", fontWeight: r.bold ? 700 : 600, color: r.bold ? "#1c5434" : BRAND.ink }}>{r.value}</span>
                </div>
                ))}
            </div>
            </div>}
          </div>
        </section>
      </div>
    </div>
  );
}
